import { useMemo, useRef, useState, useEffect } from 'react'
import { useTheme } from 'next-themes'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Copy01Icon,
  Link01Icon,
  SentIcon,
  Upload01Icon,
  Wifi01Icon,
  Tick02Icon,
  Logout01Icon,
  Sun01Icon,
  Moon02Icon,
} from '@hugeicons/core-free-icons'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Toaster } from '@/components/ui/sonner'
import { RoomQr } from '@/components/room-qr'
import { cn } from '@/lib/utils'
import { formatBytes } from '@/lib/format'
import type { RelayTransferItem } from '@/features/relay/client'
import { useTransferApp } from '@/hooks/use-transfer-app'

function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const isDark = resolvedTheme === 'dark'
  return (
    <Button
      size="sm"
      variant="ghost"
      className="h-7 w-7 p-0"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? '切换到浅色' : '切换到深色'}
    >
      {mounted ? (
        <HugeiconsIcon
          icon={isDark ? Sun01Icon : Moon02Icon}
          strokeWidth={2}
          className="size-3.5"
        />
      ) : (
        <span className="size-3.5" />
      )}
    </Button>
  )
}

function StatusPill({ ready }: { ready: boolean }) {
  const s = ready
    ? {
        label: '已就绪',
        className: 'border-transparent bg-foreground text-background',
        icon: Tick02Icon,
        spin: false,
      }
    : {
        label: '未连接',
        className: 'border-border/80 bg-background text-muted-foreground',
        icon: Wifi01Icon,
        spin: false,
      }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
        s.className,
      )}
    >
      <HugeiconsIcon
        icon={s.icon}
        strokeWidth={2}
        className={cn('size-3.5', s.spin && 'animate-spin')}
      />
      {s.label}
    </span>
  )
}

/**
 * Animated fixed notice.
 * - Enter: mount → rAF → shown (opacity/transform)
 * - Exit: shown → leaving → unmount after transitionend
 * Never affects document flow (caller must place in fixed layer).
 */
function AnimatedNotice({
  open,
  title,
  children,
  tone = 'neutral',
  action,
  onExited,
}: {
  open: boolean
  title: string
  children: React.ReactNode
  tone?: 'neutral' | 'warn'
  action?: React.ReactNode
  onExited?: () => void
}) {
  const [mounted, setMounted] = useState(open)
  const [phase, setPhase] = useState<'enter' | 'shown' | 'leaving'>(open ? 'enter' : 'leaving')
  const nodeRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (open) {
      setMounted(true)
      setPhase('enter')
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setPhase('shown'))
      })
      return () => cancelAnimationFrame(id)
    }
    setPhase('leaving')
  }, [open])

  useEffect(() => {
    if (phase !== 'leaving' || !mounted) return
    const el = nodeRef.current
    if (!el) {
      setMounted(false)
      onExited?.()
      return
    }
    let done = false
    const finish = () => {
      if (done) return
      done = true
      setMounted(false)
      onExited?.()
    }
    const onEnd = (e: TransitionEvent) => {
      if (e.target !== el) return
      if (e.propertyName !== 'opacity' && e.propertyName !== 'transform') return
      finish()
    }
    el.addEventListener('transitionend', onEnd)
    const fallback = window.setTimeout(finish, 280)
    return () => {
      el.removeEventListener('transitionend', onEnd)
      window.clearTimeout(fallback)
    }
  }, [phase, mounted, onExited])

  if (!mounted) return null

  return (
    <div
      ref={nodeRef}
      role="status"
      className={cn(
        'notice-item flex flex-col gap-2 rounded-lg border px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between',
        phase === 'shown' && 'shown',
        phase === 'leaving' && 'leaving',
        tone === 'neutral' && 'border-border bg-card/95 text-foreground shadow-sm backdrop-blur-sm',
        tone === 'warn' &&
          'border-warning/40 bg-card/95 text-foreground shadow-sm backdrop-blur-sm',
      )}
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <span
          className={cn(
            'mt-1.5 size-1.5 shrink-0 rounded-full',
            tone === 'warn' ? 'bg-warning' : 'bg-foreground/40',
          )}
          aria-hidden
        />
        <div className="min-w-0">
          <p className={cn('text-sm font-medium', tone === 'warn' && 'text-warning')}>{title}</p>
          <div className="text-muted-foreground mt-0.5 text-xs leading-relaxed">{children}</div>
        </div>
      </div>
      {action ? <div className="flex shrink-0 flex-wrap gap-2 pl-4 sm:pl-0">{action}</div> : null}
    </div>
  )
}

function FileRow({
  item,
  myRole,
  onAccept,
  onReject,
}: {
  item: RelayTransferItem
  myRole: 'host' | 'guest'
  onAccept: (item: RelayTransferItem) => void
  onReject: (item: RelayTransferItem) => void
}) {
  const isMine = item.from === myRole
  const pct =
    item.size > 0 ? Math.min(100, Math.round((item.transferred / item.size) * 100)) : 100
  const dir = isMine ? '发送' : '接收'
  const isError = item.status === 'error'
  const statusText =
    item.status === 'pending'
      ? '等待接受'
      : item.status === 'uploading'
        ? '上传中'
        : item.status === 'downloading'
          ? '下载中'
          : item.status === 'rejected'
            ? '已拒绝'
            : item.status === 'error'
              ? '失败'
              : '完成'

  return (
    <div className="flex flex-col gap-2 border-b border-border/50 py-3 transition-colors duration-150 last:border-0 hover:bg-muted/20">
      <div className="flex items-start justify-between gap-3 px-1">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium tracking-tight">{item.name}</p>
          <p className="text-muted-foreground mt-0.5 text-xs transition-colors duration-150">
            {dir} · {statusText}
            {item.status === 'uploading' || item.status === 'downloading'
              ? ` · ${pct}%`
              : ''}
          </p>
        </div>
        {item.status === 'pending' && !isMine ? (
          <div className="flex shrink-0 gap-2">
            <Button
              size="sm"
              className="h-8"
              onClick={() => onAccept(item)}
            >
              接受
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => onReject(item)}
            >
              拒绝
            </Button>
          </div>
        ) : null}
        {item.status === 'done' && !isMine ? (
          <Badge variant="secondary" className="shrink-0 font-normal">
            已接收
          </Badge>
        ) : null}
      </div>
      <div className="px-1">
        <Progress
          value={pct}
          className={cn('h-1 w-full transition-opacity duration-200', isError && 'opacity-40')}
        />
      </div>
      <div className="text-muted-foreground flex justify-between px-1 font-mono text-[11px] tabular-nums">
        <span>
          {formatBytes(item.transferred)} / {formatBytes(item.size)}
        </span>
        <span>{pct}%</span>
      </div>
      {item.error ? <p className="text-destructive px-1 text-xs">{item.error}</p> : null}
    </div>
  )
}

export default function App() {
  const app = useTransferApp()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [showQr, setShowQr] = useState(false)
  const [readyDismissed, setReadyDismissed] = useState(false)

  const roomDisplay = useMemo(() => {
    if (!app.roomCode) return null
    return app.roomCode.split('').join(' ')
  }, [app.roomCode])

  const isHostWithCode = app.role === 'host' && Boolean(app.roomCode)
  const canTransfer = app.ready
  const hasRoom = Boolean(app.roomCode)
  const myRole = app.role ?? 'host'
  const readyOpen = app.ready && !readyDismissed
  const readyCode = app.roomCode

  const onFiles = (files: FileList | null) => {
    if (!files?.length) return
    if (!app.ready) {
      toast.error('请先创建或加入房间')
      return
    }
    void app.sendFiles(files)
    toast.success(`已加入 ${files.length} 个文件到发送队列`)
  }

  // Sanitize join input: uppercase + strip non-alnum (matches server ^[A-Z0-9]{4,8}$)
  const onJoinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
    app.setJoinCode(cleaned)
  }

  const stageHint = canTransfer
    ? '已就绪 · 可以传输'
    : '创建或加入房间后即可传文件'

  return (
    <div className="bg-background text-foreground flex min-h-svh flex-col">
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-4 px-4 py-5 sm:gap-5 sm:px-6 sm:py-7">
        {/* Compact header */}
        <header className="border-border/60 flex flex-wrap items-center justify-between gap-3 border-b pb-4">
          <div className="flex items-center gap-2.5">
            <div className="bg-foreground text-background flex size-7 items-center justify-center rounded-md">
              <HugeiconsIcon icon={SentIcon} strokeWidth={2} className="size-3.5" />
            </div>
            <h1 className="text-base font-semibold tracking-tight sm:text-lg">WebFile</h1>
            {app.role ? (
              <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal">
                {app.role === 'host' ? 'Host' : 'Guest'}
              </Badge>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill ready={app.ready} />
            <ThemeToggle />
            {hasRoom ? (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => app.disconnect()}
              >
                <HugeiconsIcon icon={Logout01Icon} strokeWidth={2} data-icon="inline-start" />
                断开
              </Button>
            ) : null}
          </div>
        </header>

        {/* Fixed notices: animated enter/exit, never reflow the stage */}
        <div className="pointer-events-none fixed inset-x-0 top-3 z-40 flex justify-center px-3 sm:top-4 sm:px-4">
          <div className="flex w-full max-w-xl flex-col gap-2">
            <AnimatedNotice
              open={readyOpen}
              key={readyCode}
              title="房间已就绪"
              tone="neutral"
              action={
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => setReadyDismissed(true)}
                >
                  知道了
                </Button>
              }
            >
              复制链接或让对方扫二维码，双方都可拖放文件；web 端自动接收。
            </AnimatedNotice>
          </div>
        </div>

        {/* Stage — fills remaining viewport height on large screens */}
        <div className="grid flex-1 items-stretch gap-4 lg:grid-cols-[minmax(300px,0.85fr)_minmax(0,1.4fr)] lg:gap-5 lg:min-h-0">
          {/* Left — room */}
          <section className="border-border bg-card flex flex-col gap-4 rounded-xl border p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2">
              <p className="text-muted-foreground text-[11px] font-medium tracking-wider uppercase">
                房间
              </p>
              {app.expiresInMinutes != null ? (
                <p className="text-muted-foreground text-[11px]">
                  {app.expiresInMinutes} 分钟内有效
                </p>
              ) : null}
            </div>

            {/* Code anchor — grows to fill on large screens */}
            <div className="bg-muted/40 flex flex-1 flex-col items-center justify-center rounded-lg px-3 py-5 transition-[background-color] duration-300">
              {roomDisplay ? (
                <p className="font-mono text-2xl font-semibold tracking-[0.2em] transition-opacity duration-200 sm:text-[1.75rem]">
                  {roomDisplay}
                </p>
              ) : (
                <div className="flex flex-col items-center gap-1.5">
                  <div className="flex gap-1.5">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <span
                        key={i}
                        className="bg-foreground/12 size-2 rounded-full transition-colors duration-300"
                        aria-hidden
                      />
                    ))}
                  </div>
                  <p className="text-muted-foreground text-xs">创建后显示短码</p>
                </div>
              )}
            </div>

            {isHostWithCode && app.shareUrl ? (
              <div className="flex flex-col items-center gap-2 transition-opacity duration-300">
                <div className="hidden justify-center sm:flex">
                  <div className="rounded-lg border border-border/70 bg-white p-2">
                    <RoomQr url={app.shareUrl} size={132} />
                  </div>
                </div>
                <div className="flex w-full flex-col items-center gap-2 sm:hidden">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-full"
                    onClick={() => setShowQr((v) => !v)}
                  >
                    {showQr ? '收起二维码' : '显示二维码'}
                  </Button>
                  {showQr ? (
                    <div className="rounded-lg border border-border/70 bg-white p-2">
                      <RoomQr url={app.shareUrl} size={140} />
                    </div>
                  ) : null}
                </div>
                <p className="text-muted-foreground text-center text-[11px]">
                  扫码打开后自动加入
                </p>
              </div>
            ) : null}

            <div className="flex flex-col gap-2">
              <Button
                className="h-10 w-full text-sm"
                disabled={app.busy}
                onClick={() => void app.createRoom()}
              >
                <HugeiconsIcon icon={Link01Icon} strokeWidth={2} data-icon="inline-start" />
                创建房间
              </Button>
              {hasRoom ? (
                <Button
                  variant="outline"
                  className="h-9 w-full text-sm"
                  onClick={() => void app.copyRoom()}
                >
                  <HugeiconsIcon icon={Copy01Icon} strokeWidth={2} data-icon="inline-start" />
                  复制链接
                </Button>
              ) : null}
            </div>

            <div className="border-border/70 border-t pt-3">
              <p className="text-muted-foreground mb-2 text-[11px]">或输入短码加入</p>
              <div className="flex gap-2">
                <Input
                  value={app.joinCode}
                  onChange={onJoinChange}
                  placeholder="AB12CD"
                  maxLength={8}
                  className="h-10 flex-1 font-mono text-sm tracking-widest uppercase"
                  autoComplete="off"
                  inputMode="text"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void app.joinRoom()
                  }}
                />
                <Button
                  variant="outline"
                  className="h-10 shrink-0 px-4 text-sm"
                  disabled={app.busy}
                  onClick={() => void app.joinRoom()}
                >
                  加入
                </Button>
              </div>
            </div>
          </section>

          {/* Right — transfer */}
          <section
            className={cn(
              'border-border bg-card flex flex-col gap-4 rounded-xl border p-4 transition-colors duration-300 sm:p-5',
              canTransfer ? 'border-foreground/15' : 'border-border',
            )}
          >
            <div>
              <p className="text-sm font-medium tracking-tight">传输</p>
              <p className="text-muted-foreground text-xs">{stageHint}</p>
            </div>

            {canTransfer ? (
              <div
                className={cn(
                  'group relative flex min-h-44 flex-1 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-8 text-center transition-[border-color,background-color] duration-200 ease-out',
                  dragOver
                    ? 'border-foreground bg-muted/50'
                    : 'border-foreground/30 bg-muted/15 hover:bg-muted/30 hover:border-foreground/50',
                )}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragOver(true)
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragOver(false)
                  onFiles(e.dataTransfer.files)
                }}
                onClick={() => inputRef.current?.click()}
              >
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    onFiles(e.target.files)
                    e.target.value = ''
                  }}
                />
                <div className="border-border bg-background flex size-11 items-center justify-center rounded-full border transition-colors duration-200 group-hover:border-foreground/30">
                  <HugeiconsIcon
                    icon={Upload01Icon}
                    strokeWidth={2}
                    className="text-muted-foreground size-5 transition-colors duration-200 group-hover:text-foreground"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium">拖放文件到此处，或点击选择</p>
                  <p className="text-muted-foreground mt-1 text-xs">多文件 · 经中转自动同步</p>
                </div>
              </div>
            ) : (
              <div className="bg-muted/25 flex flex-1 flex-col items-center justify-center gap-2 rounded-lg px-6 py-8 text-center sm:py-10">
                <div className="bg-background text-muted-foreground flex size-9 items-center justify-center rounded-full border border-border/80">
                  <HugeiconsIcon icon={Upload01Icon} strokeWidth={2} className="size-4" />
                </div>
                <p className="text-sm font-medium text-foreground/90">连接后在此发送文件</p>
                <p className="text-muted-foreground max-w-[16rem] text-xs leading-relaxed">
                  在左侧创建房间并分享，或输入短码加入
                </p>
              </div>
            )}

            {app.items.length > 0 ? (
              <div className="flex max-h-[24rem] flex-col overflow-y-auto">
                {app.items.map((item) => (
                  <FileRow
                    key={item.id || item.name}
                    item={item}
                    myRole={myRole}
                    onAccept={(it) => void app.acceptFile(it)}
                    onReject={(it) => app.rejectFile(it)}
                  />
                ))}
              </div>
            ) : null}
          </section>
        </div>

        <footer className="text-muted-foreground/60 pb-1 pt-1 text-center text-[11px]">
          文件经 EdgeOne 中转同步 · 房间短码配对
        </footer>
      </div>

      <Toaster position="top-center" theme="system" richColors closeButton />
    </div>
  )
}